import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast } from '@vario-connect/frontend-design';
import {
  createRevokeState,
  pressRevoke,
  revokeLabel,
  syncRevokeState,
  type RevokeState,
} from '../../lib/revoke-state.js';
import {
  formatConnections,
  formatOauthAccount,
  validateAccountName,
  type OauthAccountRow,
  type OauthConnectionRow,
} from '../../lib/mcp-oauth-flow.js';

export interface OauthCardCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
}

/** Server-URL fest, nie ueber *.supabase.co (Projektregel). */
const MCP_SERVER_URL = 'https://kaufland-api.goe-connect.de/functions/v1/kaufland-mcp';

/**
 * Statisches Geruest. Wird synchron gesetzt, damit die Karte auch dann sichtbar
 * ist, wenn das Nachladen scheitert — dieselbe Bauart wie die Token-Karte.
 */
export function renderOauthCardShell(): string {
  return `
    <article class="mp-card" id="kl-oauth-card">
      <div class="mp-head">
        <span class="mp-name">Zugang für KI-Assistenten</span>
        <span class="mp-badge" id="kl-oauth-badge" hidden><span class="d"></span></span>
      </div>

      <div class="mp-sec">
        <div class="mp-lab">Server-URL</div>
        <div class="mp-field">
          <div class="v-input"><input type="text" id="kl-oauth-url" readonly value="${MCP_SERVER_URL}" /></div>
        </div>
        <div class="mp-action">
          <button type="button" class="mp-btn" id="kl-oauth-copy-url">
            <span class="v-icon fa-regular fa-copy"></span>
            Server-URL kopieren
          </button>
        </div>
        <div class="mp-note">Mehr braucht dein KI-Client nicht. Die Anmeldung passiert danach im Browser, es wird kein Token in die Konfiguration eingetragen.</div>
      </div>

      <div class="mp-sec border">
        <div class="mp-lab">Dein Zugang</div>
        <p class="mp-hint" id="kl-oauth-zustand">Lade …</p>

        <div id="kl-oauth-anlegen" hidden>
          <div class="mp-field">
            <span class="mp-flab">Name</span>
            <div class="v-input"><input type="text" id="kl-oauth-name" maxlength="60" placeholder="z. B. Team Einkauf" /></div>
          </div>
          <div class="mp-action">
            <button type="button" class="mp-btn" id="kl-oauth-create">
              <span class="v-icon fa-regular fa-plus"></span>
              Zugang erzeugen
            </button>
            <span class="mp-msg" id="kl-oauth-create-msg"></span>
          </div>
        </div>

        <div class="mp-action" id="kl-oauth-revoke-wrap" hidden>
          <button type="button" class="mp-btn" id="kl-oauth-revoke-account">
            <span class="v-icon fa-regular fa-ban"></span>
            Widerrufen
          </button>
        </div>
        <div class="mp-note" id="kl-oauth-revoke-note" hidden>Ein Widerruf trennt ALLE verbundenen Clients auf einmal. Um nur einen einzelnen zu lösen, unten „Trennen" benutzen.</div>

        <div class="mp-sec" id="kl-oauth-reveal" hidden>
          <div class="mp-lab">Geheimnis, jetzt sichern</div>
          <p class="mp-hint">Der Klartext wird aus Sicherheitsgründen nur dieses eine Mal angezeigt und danach nirgends gespeichert. Bitte jetzt kopieren; ein verlorenes Geheimnis lässt sich nicht erneut anzeigen, nur widerrufen und neu erzeugen.</p>
          <div class="mp-field">
            <div class="v-input"><input type="text" id="kl-oauth-reveal-value" readonly value="" /></div>
          </div>
          <div class="mp-action">
            <button type="button" class="mp-btn" id="kl-oauth-copy">
              <span class="v-icon fa-regular fa-copy"></span>
              Geheimnis kopieren
            </button>
            <button type="button" class="mp-btn" id="kl-oauth-reveal-done">
              <span class="v-icon fa-regular fa-check"></span>
              Fertig, Geheimnis ist gesichert
            </button>
          </div>
        </div>
      </div>

      <div class="mp-sec border">
        <div class="mp-lab">Verbundene Clients</div>
        <div id="kl-oauth-connections">Lade …</div>
      </div>

      <div class="mp-sec border">
        <div class="mp-lab">So verbindest du dich</div>
        <ol class="mp-hint" style="padding-left:18px;margin:0;">
          <li>Oben einen Zugang erzeugen und das Geheimnis kopieren.</li>
          <li>Im KI-Client einen Connector auf die Server-URL anlegen — <strong>ohne</strong> Request headers. Ein eingetragener Header verhindert die Anmeldung.</li>
          <li>Der Client öffnet ein Browser-Fenster. Dort das Geheimnis eingeben und wählen, ob der Client nur lesen oder auch ändern darf.</li>
        </ol>
        <div class="mp-note">Den Umfang kannst du später ändern, indem du die Verbindung im Client neu freigibst.</div>
      </div>
    </article>`;
}

export async function initOauthCard(host: HTMLElement, ctx: OauthCardCtx): Promise<void> {
  const root = host.querySelector<HTMLElement>('#kl-oauth-card');
  if (!root) return;

  const zustandEl = root.querySelector<HTMLElement>('#kl-oauth-zustand')!;
  const badgeEl = root.querySelector<HTMLElement>('#kl-oauth-badge')!;
  const anlegenEl = root.querySelector<HTMLElement>('#kl-oauth-anlegen')!;
  const nameInput = root.querySelector<HTMLInputElement>('#kl-oauth-name')!;
  const createBtn = root.querySelector<HTMLButtonElement>('#kl-oauth-create')!;
  const createMsgEl = root.querySelector<HTMLElement>('#kl-oauth-create-msg')!;
  const revokeWrapEl = root.querySelector<HTMLElement>('#kl-oauth-revoke-wrap')!;
  const revokeNoteEl = root.querySelector<HTMLElement>('#kl-oauth-revoke-note')!;
  const revokeAccountBtn = root.querySelector<HTMLButtonElement>('#kl-oauth-revoke-account')!;
  const revealBox = root.querySelector<HTMLElement>('#kl-oauth-reveal')!;
  const revealInput = root.querySelector<HTMLInputElement>('#kl-oauth-reveal-value')!;
  const copyBtn = root.querySelector<HTMLButtonElement>('#kl-oauth-copy')!;
  const revealDoneBtn = root.querySelector<HTMLButtonElement>('#kl-oauth-reveal-done')!;
  const copyUrlBtn = root.querySelector<HTMLButtonElement>('#kl-oauth-copy-url')!;
  const urlInput = root.querySelector<HTMLInputElement>('#kl-oauth-url')!;
  const connEl = root.querySelector<HTMLElement>('#kl-oauth-connections')!;

  let account: OauthAccountRow | null = null;
  let connections: OauthConnectionRow[] = [];
  // ZWEI getrennte Zustaende: Der Widerruf des Zugangs und das Trennen einer
  // einzelnen Verbindung sind verschiedene Vorgaenge mit verschiedenen Folgen.
  // Ein gemeinsamer Zustand koennte einen scharfen Klick auf den falschen
  // Vorgang uebertragen.
  let accountRevoke: RevokeState = createRevokeState();
  let connRevoke: RevokeState = createRevokeState();
  const laufendeWiderrufe = new Set<string>();

  /** Setzt die Plakette in der Kartenkopfzeile. Leerer Text blendet sie aus. */
  function setzeBadge(art: 'ok' | 'off' | null, text: string): void {
    if (art === null) {
      badgeEl.hidden = true;
      return;
    }
    badgeEl.hidden = false;
    badgeEl.className = `mp-badge ${art}`;
    badgeEl.replaceChildren(
      Object.assign(document.createElement('span'), { className: 'd' }),
      document.createTextNode(text),
    );
  }

  function zeichneZustand(): void {
    const f = formatOauthAccount(account);
    if (!f.vorhanden || f.widerrufen) {
      zustandEl.textContent = f.vorhanden
        ? 'Der bisherige Zugang ist widerrufen. Erzeuge einen neuen, um dich wieder verbinden zu können.'
        : f.zustandText;
      anlegenEl.hidden = false;
      revokeWrapEl.hidden = true;
      revokeNoteEl.hidden = true;
      setzeBadge('off', 'Kein Zugang');
      return;
    }
    anlegenEl.hidden = true;
    zustandEl.textContent =
      `${f.row!.name} · ${f.row!.secret_prefix}… · angelegt ${f.erstelltText} · ` +
      `zuletzt verwendet ${f.letzteNutzungText}`;
    revokeWrapEl.hidden = false;
    revokeNoteEl.hidden = false;
    // Die Beschriftung kommt aus revokeLabel und wechselt beim ersten Klick auf
    // "Wirklich widerrufen?" — das Icon davor bleibt stehen.
    const beschriftung = revokeLabel(accountRevoke, f.row!.id);
    revokeAccountBtn.replaceChildren(
      Object.assign(document.createElement('span'), { className: 'v-icon fa-regular fa-ban' }),
      document.createTextNode(beschriftung),
    );
    revokeAccountBtn.disabled = laufendeWiderrufe.has(f.row!.id);
    setzeBadge('ok', 'Aktiv');
  }

  /**
   * Kein Design-System-Baustein fuer Tabellen vorhanden — dieselbe Bauart wie
   * die uebrigen Tabellen der App: klassenloses `<table>`, Zustand als
   * `mp-badge`, Aktion als `mp-btn`.
   *
   * Gebaut wird per DOM-API und nicht per innerHTML: Client-Namen kommen aus
   * einer Registrierung, die jeder Fremde anlegen kann (offene Dynamic Client
   * Registration). `textContent` schliesst das Einschleusen von Markup aus.
   */
  function zeichneVerbindungen(): void {
    const rows = formatConnections(connections);
    if (rows.length === 0) {
      connEl.replaceChildren(
        Object.assign(document.createElement('p'), {
          className: 'mp-hint',
          textContent: 'Noch kein Client verbunden.',
        }),
      );
      return;
    }

    const tabelle = document.createElement('table');
    const thead = document.createElement('thead');
    const kopfZeile = document.createElement('tr');
    for (const text of ['Client', 'Umfang', 'Verbunden', 'Zustand', '']) {
      const th = document.createElement('th');
      th.textContent = text;
      kopfZeile.appendChild(th);
    }
    thead.appendChild(kopfZeile);
    tabelle.appendChild(thead);

    const tbody = document.createElement('tbody');
    for (const r of rows) {
      const tr = document.createElement('tr');
      for (const text of [r.anzeigeName, r.scopeText, r.erstelltText]) {
        const td = document.createElement('td');
        td.textContent = text;
        tr.appendChild(td);
      }

      const tdZustand = document.createElement('td');
      const badge = document.createElement('span');
      badge.className = r.revoked_at === null ? 'mp-badge ok' : 'mp-badge off';
      badge.appendChild(Object.assign(document.createElement('span'), { className: 'd' }));
      badge.appendChild(document.createTextNode(r.zustandText));
      tdZustand.appendChild(badge);
      tr.appendChild(tdZustand);

      const tdBtn = document.createElement('td');
      if (r.revoked_at === null) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mp-btn';
        btn.appendChild(
          Object.assign(document.createElement('span'), { className: 'v-icon fa-regular fa-link-slash' }),
        );
        btn.appendChild(
          document.createTextNode(revokeLabel(connRevoke, r.id) === 'Widerrufen' ? 'Trennen' : 'Wirklich trennen?'),
        );
        btn.disabled = laufendeWiderrufe.has(r.id);
        btn.addEventListener('click', () => void aufVerbindungTrennenKlick(r.id));
        tdBtn.appendChild(btn);
      }
      tr.appendChild(tdBtn);
      tbody.appendChild(tr);
    }
    tabelle.appendChild(tbody);
    connEl.replaceChildren(tabelle);
  }

  async function ladeAlles(): Promise<void> {
    const { data: aData, error: aErr } = await ctx.supabase
      .schema('kaufland')
      .from('mcp_oauth_accounts')
      .select('id,name,secret_prefix,created_at,last_used_at,revoked_at')
      .order('created_at', { ascending: false })
      .limit(1);
    if (aErr) {
      zustandEl.textContent = `Zugang konnte nicht geladen werden: ${aErr.message}`;
      return;
    }
    account = (aData?.[0] ?? null) as OauthAccountRow | null;

    // client_name kommt direkt aus der Zeile, nicht aus einem Join: die Tabelle
    // mcp_oauth_clients ist mandantenunabhaengig und hat deshalb kein Leserecht
    // fuer authenticated. Plan 3 schreibt den Namen beim Ausstellen mit.
    const { data: cData, error: cErr } = await ctx.supabase
      .schema('kaufland')
      .from('mcp_oauth_tokens')
      .select('id,client_name,scope,created_at,access_expires_at,revoked_at');
    if (cErr) {
      connEl.textContent = `Verbindungen konnten nicht geladen werden: ${cErr.message}`;
    } else {
      connections = (cData ?? []) as unknown as OauthConnectionRow[];
    }

    // Vertrag aus mcp-token-flow: bei JEDEM Neuladen entschaerfen, nicht nur
    // nach einem Klick. Sonst haelt der Nutzer den naechsten Klick faelschlich
    // fuer den ersten. Gilt fuer BEIDE Zustaende.
    connRevoke = syncRevokeState(connRevoke, connections);
    accountRevoke = syncRevokeState(accountRevoke, account ? [account] : []);

    zeichneZustand();
    zeichneVerbindungen();
  }

  async function aufAnlegenKlick(): Promise<void> {
    const fehler = validateAccountName(nameInput.value);
    if (fehler) {
      createMsgEl.textContent = fehler;
      return;
    }
    createBtn.disabled = true;
    createMsgEl.textContent = 'Wird angelegt …';
    const { data, error } = await ctx.supabase.rpc('kaufland_create_mcp_oauth_account', {
      _name: nameInput.value.trim(),
    });
    createBtn.disabled = false;
    if (error) {
      createMsgEl.textContent = `Fehlgeschlagen: ${error.message}`;
      return;
    }
    const zeile = Array.isArray(data) ? data[0] : data;
    const klartext = (zeile as { secret?: string })?.secret ?? null;
    createMsgEl.textContent = '';
    nameInput.value = '';
    if (klartext) {
      revealInput.value = klartext;
      revealBox.hidden = false;
    }
    await ladeAlles();
  }

  /** Trennt EINE Verbindung. Der Zugang bleibt bestehen. */
  async function aufVerbindungTrennenKlick(id: string): Promise<void> {
    if (laufendeWiderrufe.has(id)) return;
    const ergebnis = pressRevoke(connRevoke, id);
    connRevoke = ergebnis.state;
    if (ergebnis.action !== 'revoke') {
      zeichneVerbindungen();
      return;
    }
    laufendeWiderrufe.add(id);
    zeichneVerbindungen();
    const { error } = await ctx.supabase.rpc('kaufland_revoke_mcp_oauth_connection', { _id: id });
    laufendeWiderrufe.delete(id);
    if (error) showToast(`Trennen fehlgeschlagen: ${error.message}`, 'error');
    else showToast('Verbindung getrennt', 'success');
    connRevoke = createRevokeState();
    await ladeAlles();
  }

  /** Widerruft den ZUGANG und damit alles, was daraus entstanden ist. */
  async function aufZugangWiderrufenKlick(): Promise<void> {
    const id = account?.id;
    if (!id || laufendeWiderrufe.has(id)) return;
    const ergebnis = pressRevoke(accountRevoke, id);
    accountRevoke = ergebnis.state;
    if (ergebnis.action !== 'revoke') {
      zeichneZustand();
      return;
    }
    laufendeWiderrufe.add(id);
    zeichneZustand();
    const { error } = await ctx.supabase.rpc('kaufland_revoke_mcp_oauth_account', { _id: id });
    laufendeWiderrufe.delete(id);
    if (error) showToast(`Widerruf fehlgeschlagen: ${error.message}`, 'error');
    else showToast('Zugang widerrufen, alle Verbindungen getrennt', 'success');
    accountRevoke = createRevokeState();
    await ladeAlles();
  }

  copyUrlBtn.addEventListener('click', () => {
    urlInput.select();
    void navigator.clipboard.writeText(urlInput.value);
    showToast('Server-URL kopiert', 'success');
  });
  createBtn.addEventListener('click', () => void aufAnlegenKlick());
  revokeAccountBtn.addEventListener('click', () => void aufZugangWiderrufenKlick());
  copyBtn.addEventListener('click', () => {
    revealInput.select();
    void navigator.clipboard.writeText(revealInput.value);
    showToast('Geheimnis kopiert', 'success');
  });
  revealDoneBtn.addEventListener('click', () => {
    // Klartext aus dem DOM entfernen, nicht nur verstecken.
    revealInput.value = '';
    revealBox.hidden = true;
  });

  await ladeAlles();
}
